/**
 * Camada central de segurança / LGPD.
 *
 * Reúne limites e helpers usados por:
 *  - Web Worker (validação de payload e bloqueio XXE)
 *  - UI principal (purge de memória no logout)
 *  - Banner de cookies (consentimento LGPD Art. 7º, I)
 */

/* ===== Limites de processamento (anti-DoS local) ===== */

/** Tamanho máximo do ZIP enviado pelo usuário (bytes). 200MB. */
export const MAX_ZIP_SIZE = 200 * 1024 * 1024;

/** Tamanho máximo de UM único XML descompactado (bytes). 50MB. */
export const MAX_XML_SIZE = 50 * 1024 * 1024;

/** Quantidade máxima de XMLs em um único lote — defesa contra zip bomb. */
export const MAX_XMLS_PER_BATCH = 200_000;

/** Razão de expansão suspeita (descompactado / compactado). */
export const MAX_ZIP_EXPANSION_RATIO = 200;

/* ===== Detecção de payload XML malicioso ===== */

/**
 * Bloqueia DOCTYPE e ENTITY — vetores clássicos de:
 *  - XXE (XML External Entity)
 *  - Billion Laughs / Quadratic Blowup
 *  - SSRF via SYSTEM/PUBLIC
 *
 * Inspeção é feita nos primeiros 8KB (cabeçalho do XML) — suficiente
 * para qualquer DOCTYPE válido, e barato de executar.
 */
export function isXmlPayloadDangerous(xmlSnippet: string): { dangerous: boolean; reason?: string } {
  const head = xmlSnippet.slice(0, 8192).toUpperCase();
  if (head.includes("<!DOCTYPE")) {
    return { dangerous: true, reason: "DOCTYPE não permitido (proteção XXE)" };
  }
  if (head.includes("<!ENTITY")) {
    return { dangerous: true, reason: "Entidades XML não permitidas (proteção Billion Laughs)" };
  }
  // Detecta ENTITY no corpo também — defesa em profundidade.
  if (xmlSnippet.includes("<!ENTITY")) {
    return { dangerous: true, reason: "Entidades XML detectadas no corpo" };
  }
  return { dangerous: false };
}

/* ===== LGPD: purga de memória ===== */

/**
 * Limpa caches voláteis do navegador que possam conter
 * dados fiscais residuais (sessionStorage, caches do React Query, etc).
 *
 * Não toca em localStorage (sessão Supabase + tema + consentimento ficam).
 */
export function purgeVolatileCaches() {
  try {
    sessionStorage.clear();
  } catch {
    /* sessionStorage indisponível — ignorar */
  }

  // Sugere coleta de lixo (somente Chrome/Edge com flag, mas não custa).
  const w = window as unknown as { gc?: () => void };
  if (typeof w.gc === "function") {
    try { w.gc(); } catch { /* noop */ }
  }
}

/* ===== Consentimento de cookies (LGPD Art. 7º, I) ===== */

const CONSENT_KEY = "lgpd.cookieConsent.v1";

export type ConsentStatus = "accepted" | "rejected" | null;

export function getCookieConsent(): ConsentStatus {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    if (v === "accepted" || v === "rejected") return v;
    return null;
  } catch {
    return null;
  }
}

export function setCookieConsent(status: Exclude<ConsentStatus, null>) {
  try {
    localStorage.setItem(CONSENT_KEY, status);
  } catch {
    /* noop */
  }
}

/* ===== Metadados de DPO / Encarregado (LGPD Art. 41) ===== */

export const DPO_INFO = {
  nome: "Encarregado de Dados (DPO)",
  email: "privacidade@extratorfiscal.app",
  versaoPolitica: "1.1",
  ultimaAtualizacao: "2026-05-04",
};
